import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { Page } from '@app/admin/model/spring-pageable';
import { AppConfig, appConfigService } from '@app/config';
import { TreeViewProviderDirective } from '@app/providers/tree-view-provider.service';
import { FilterContainerUtil } from '@app/shared/util/filter-container-util';
import { CommonFilterViewComponent } from '@ntap/common';
import {
  DynamicComponent,
  FilterView,
  FilterViewProviderDirective,
  ListViewProviderDirective
} from '@ntap/core';
import { MessageService, TreeDragDropService, TreeNode } from 'primeng/api';
import { of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import {
  HierarchyNode,
  HierarchyPosition
} from '../hierarhija-pozicija/queries';
import {
  DEFAULT_PAGE_LIMIT,
  FQAN_glavni_naziv,
  FQAN_jedgrad_id,
  FQAN_nadred_jedgrad,
  FQAN_razina,
  HierarhijaComponent,
  INITAL_ROOT_VIEWSTATE,
  Razina
} from '../hierarhija/hierarhija.component';
import { EarhivTreeNode } from '../hierarhija/types';
import { ParentListViewProvider } from '../pretraga-kataloga/providers/parent-list-view.provider';

const PARAM_folderOpenView = '$folderOpenView';
const VIEW_FOLDERS = 'dokumentacija_folders';
const VIEW_DOCUMENTS = 'dokumentacija';
const VIEW_DOCUMENTS_PREDMET = 'dokumentacija_predmet';
const VIEW_PRETRAGA = 'dokumentacija_pretraga';
const VIEW_PRETRAGA_FILTER = 'dokumentacija_pretraga_filter';
const INIT_NODE_VIEWSTATE = {
  // $paging_offset: 0,
  // $paging_limit: 100,
  // [FQAN_razina]: -1, // force empty data!
  [FQAN_nadred_jedgrad]: -1 // force empty data!
};
const DEFAULT_FILTER_STATE = {
  // [FQAN_glavni_naziv]: 'root' // debug: izbaciti !!!
};

const ICON_FOLDER = 'pi pi-folder';
const ICON_PREDMET = 'pi pi-credit-card';
const ICON_DOKUMENT = 'pi pi-file';

@Component({
  selector: 'app-dokumentacija-hierarhija',
  templateUrl: '../hierarhija/hierarhija.component.html',
  styleUrls: ['./dokumentacija-hierarhija.component.scss'],
  providers: [
    TreeDragDropService,
    MessageService,
    TreeViewProviderDirective,
    FilterViewProviderDirective,
    {
      provide: ListViewProviderDirective,
      useExisting: TreeViewProviderDirective
    },
    {
      provide: ParentListViewProvider,
      useExisting: TreeViewProviderDirective
    }
  ]
})
export class DokumentacijaHierarhijaComponent
  extends HierarhijaComponent
  implements AfterViewInit
{
  private readonly initialRootViewState = Object.assign(
    INITAL_ROOT_VIEWSTATE,
    DEFAULT_FILTER_STATE
  );

  @ViewChild('filterContainer') filterContainer: DynamicComponent;

  constructor(
    msg: MessageService,
    router: Router,
    listViewProvider: TreeViewProviderDirective,
    filterViewProviderDirective: FilterViewProviderDirective<FilterView>,
    protected cdr: ChangeDetectorRef,
    @Inject(appConfigService) private config: AppConfig,
    private http: HttpClient
  ) {
    super(msg, router, listViewProvider, filterViewProviderDirective);
    this.title = 'Dokumentacija';
    // this.showToolbarTitle = false;
    this.showCustomToolbar = false;
    this.filterViewName = VIEW_PRETRAGA_FILTER;
    this.rootViewName = VIEW_FOLDERS;
    this.nodesViewName = VIEW_FOLDERS;
    this.propagateSelectionUp = false;
    this.propagateSelectionDown = false;
    this.rootNodesSelectable = true;
    this.selectionMode = 'checkbox';
    this.useChildrenListView = true;
    this.childrenListViewName = VIEW_DOCUMENTS;
    this.useMergedSearchView = true;
    this.mergedSearchViewName = VIEW_PRETRAGA;
    this.mergedSearchViewState = {};
    this.mergedSearchViewStateFilter = {};
  }

  ngAfterViewInit(): void {
    if (this.filterContainer) {
      const filterComponent =
        this.filterContainer.getViewComponent() as CommonFilterViewComponent;
      FilterContainerUtil.separateOsnovnaNaprednaPretraga(filterComponent);
    }

    if (!this.useChildrenListView || this.selectionMode != 'checkbox') {
      return;
    }
    setTimeout(() => {
      if (!this.tree) {
        return;
      }
      const originalOnNodeClick = this.tree.onNodeClick.bind(this.tree);
      this.tree.onNodeClick = (event: any, node: TreeNode<any>) => {
        const targetClassList = event.srcElement.classList;
        const isCheckboxClick =
          targetClassList &&
          Array.from(targetClassList).some((className: any) =>
            className.includes('checkbox')
          );
        if (!isCheckboxClick) {
          this.onNodeSelect({ node: node });
          event.preventDefault();
        } else {
          originalOnNodeClick(event, node);
        }
      };
    }, 1200);
  }

  protected override getInitRootViewState() {
    return this.initialRootViewState;
  }

  protected override getInitViewState() {
    return INIT_NODE_VIEWSTATE;
  }

  protected override newExpandedChildrenViewState(node: EarhivTreeNode) {
    const requestState: any = Object.assign(
      {},
      this.getInitRootViewState(),
      this.listViewProvider.viewState
    );
    requestState[FQAN_nadred_jedgrad] = node.data[FQAN_jedgrad_id];
    return requestState;
  }

  protected override getRootNodeLabel(element: any) {
    return this.getNodeLabel(element);
  }

  protected override getNodeLabel(element: any) {
    return element[FQAN_glavni_naziv];
  }

  protected override newRootNode(element: any): EarhivTreeNode {
    const newRootNode = super.newRootNode(element);
    // prilikom pretrage se flata lista pa mijenjamo ikone rezultata
    if (element[FQAN_razina] > Razina.GRUPA) {
      newRootNode.icon = this.getIconClass(element);
    }
    newRootNode.lockedIconClass = super.getNodeLockIcon(element);
    return newRootNode;
  }

  protected override getRootNodeIcon(): string {
    return ICON_FOLDER;
  }

  protected override newNode(element: any): EarhivTreeNode {
    const newNode = super.newNode(element);
    newNode.icon = this.getIconClass(element);
    return newNode;
  }

  private getIconClass(element: any): string {
    switch (element[FQAN_razina]) {
      case Razina.FOND:
      case Razina.ZBIRKA:
      case Razina.PODFOND:
      case Razina.SERIJA:
      case Razina.GRUPA:
        return ICON_FOLDER;
      case Razina.PREDMET:
        return ICON_PREDMET;
      case Razina.DOKUMENT:
        return ICON_DOKUMENT;
      default:
        return '';
    }
  }

  override onNodeSelect(event: any): void {
    const data = event.node.data;
    this.childrenListViewName =
      data?.[PARAM_folderOpenView] ||
      (data?.[FQAN_razina] == Razina.PREDMET
        ? VIEW_DOCUMENTS_PREDMET
        : VIEW_DOCUMENTS);
    super.onNodeSelect(event);
  }

  protected override isChildrenViewAvailable(node: any): boolean {
    return node.data[FQAN_razina] <= Razina.PREDMET;
  }

  findNodePath(id: number) {
    this.listViewProvider.closeNodes();
    this.isTreeShown = true;
    this.selectedFiles = [];

    this.http
      .get<{
        breadcrumbs: HierarchyPosition[];
        hierarchy: Page<HierarchyNode>;
      }>(`${this.config.apiHost}hierarchy/${id}`)
      .subscribe((response) => {
        const breadcrumbs = response.breadcrumbs;

        if (!breadcrumbs || breadcrumbs.length === 0) {
          console.error('Breadcrumbs not found for the node.');
          return;
        }
        const rootBreadcrumb = breadcrumbs[0];

        const findAndExpandRoot = () => {
          this.listViewProvider.roots$
            .pipe(
              take(1),
              switchMap((roots) => {
                const foundRootNode = roots.find(
                  (node) => node[FQAN_jedgrad_id] === rootBreadcrumb.id
                );

                if (!foundRootNode) {
                  // Load more roots and then retry the search
                  return this.loadMoreRoots().pipe(
                    switchMap(() => this.listViewProvider.roots$),
                    map((updatedRoots) =>
                      updatedRoots.find(
                        (node) => node[FQAN_jedgrad_id] === rootBreadcrumb.id
                      )
                    )
                  );
                }
                return of(foundRootNode);
              })
            )
            .subscribe((foundRootNode) => {
              if (foundRootNode) {
                this.expandNodePath(rootBreadcrumb, breadcrumbs, 1);
              } else {
                // If still not found after loading more, try again
                findAndExpandRoot();
              }
            });
        };

        findAndExpandRoot();
      });
  }

  expandNodePath(
    currentNode: HierarchyPosition,
    breadcrumbs: HierarchyPosition[],
    index: number
  ) {
    const foundNode = this.listViewProvider.idToNodeMap[currentNode.id];
    const nextNodeInPath = breadcrumbs[index];
    if (index >= breadcrumbs.length || nextNodeInPath.razina > Razina.PREDMET) {
      this.selectedFiles = foundNode;
      this.onNodeSelect({ node: foundNode });
      return;
    }

    this.nodeExpandObservable({ node: foundNode }).subscribe(() => {
      if (currentNode.razina < Razina.PREDMET) {
        // toggle current node
        this.listViewProvider.expandNode(currentNode.id);
      }
      const findChild = () => {
        const foundChildNode =
          this.listViewProvider.idToNodeMap[nextNodeInPath.id];
        if (!foundChildNode) {
          const requestState = Object.assign(
            {},
            this.listViewProvider.viewState,
            {
              $paging_offset: foundNode.parent?.offset,
              $paging_limit: 100,
              [FQAN_nadred_jedgrad]: foundNode.id
            }
          );
          this.listViewProvider
            .loadMore(foundNode, requestState, DEFAULT_PAGE_LIMIT)
            .subscribe((res) => {
              if (res.length < DEFAULT_PAGE_LIMIT) {
                return;
              } else {
                findChild();
              }
            });
        }
      };
      findChild();

      this.expandNodePath(nextNodeInPath, breadcrumbs, index + 1);
    });
  }
}
